import React, { useState, useRef, useEffect } from "react";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import axios from "axios";
import useCreateArticle from "./useCreateArticle";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import ApiService from "../../../service/ApiService";
import { apiPaths } from "../../../service/apiPaths";
import { ArrowLeft } from "lucide-react";
import { Autocomplete, TextField, Chip } from "@mui/material";

// CUSTOM VIDEO BLOT - This is the key solution!
const BlockEmbed = Quill.import("blots/block/embed");

class VideoBlot extends BlockEmbed {
  static create(url: string) {
    const node = super.create();
    node.setAttribute("controls", "true");
    node.setAttribute("preload", "metadata");
    node.setAttribute(
      "style",
      "width: 100%; max-width: 100%; height: auto; display: block; margin: 10px auto; border-radius: 8px;"
    );

    // Add source element for better browser compatibility
    const source = document.createElement("source");
    source.setAttribute("src", url);
    source.setAttribute("type", "video/mp4");
    node.appendChild(source);

    // Fallback text
    const fallbackText = document.createTextNode("Your browser does not support the video tag.");
    node.appendChild(fallbackText);

    return node;
  }

  static value(node: HTMLElement) {
    const source = node.querySelector("source");
    return source ? source.getAttribute("src") : node.getAttribute("src");
  }

  static formats(node: HTMLElement) {
    const format: any = {};
    if (node.hasAttribute("height")) {
      format.height = node.getAttribute("height");
    }
    if (node.hasAttribute("width")) {
      format.width = node.getAttribute("width");
    }
    return format;
  }
}

VideoBlot.blotName = "customVideo";
VideoBlot.tagName = "video";

// Register the custom video blot
Quill.register(VideoBlot);

const CreateArticle: React.FC = () => {
  const [title, setTitle] = useState<string>("");
  const [content, setContent] = useState<string>("");
  const [introImage, setIntroImage] = useState<string>("");
  const [isPreview, setIsPreview] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [intro, setIntro] = useState<string>("");
  const [standard, setStandard] = useState("");
  const [courseName, setCourseName] = useState("");
  const [categoryIds, setCategoryIds] = useState<string[]>([]);
  const [headerImage, setHeaderImage] = useState<File | null>(null);
  const [headerImagePreview, setHeaderImagePreview] = useState("");
  const [filteredCourses, setFilteredCourses] = useState<any[]>([]);
  const navigate = useNavigate();

  const quillRef = useRef<ReactQuill | null>(null);

  const { handleSubmit, categories, courses, standards } = useCreateArticle();
  console.log(categories);
  const [submitLoader, setSubmitLoader] = useState<boolean>(false);

  useEffect(() => {
    if (standard) {
      const newCourses = courses.filter((course) => course.standard === standard);
      setFilteredCourses(newCourses);
    } else {
      setFilteredCourses([]);
    }
  }, [standard]);

  const handleImageUpload = async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await ApiService({
        method: "POST",
        endpoint: apiPaths.uploadFile,
        headers: {
          "Content-type": "multipart/form-data",
        },
        data: formData,
      });
      console.log(response);
      const imageUrl = response.file;
      console.log(imageUrl);

      setIntroImage(imageUrl);

      const editor = quillRef.current?.getEditor();
      const range = editor?.getSelection();
      if (editor && range) {
        editor.insertEmbed(range.index, "image", imageUrl);
        editor.insertText(range.index + 1, "\n");
        editor.setSelection(range.index + 2, 0);
      }
    } catch (error) {
      console.error("Error uploading image:", error);
    }
  };

  // FIXED video upload using custom blot
  const handleVideoUpload = async (file: File) => {
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await ApiService({
        method: "POST",
        endpoint: apiPaths.uploadFile,
        headers: {
          "Content-type": "multipart/form-data",
        },
        data: formData,
      });
      console.log("Upload response:", response);
      const videoUrl = response.file;
      console.log("Video URL:", videoUrl);

      const editor = quillRef.current?.getEditor();
      const range = editor?.getSelection();

      if (editor && range) {
        // Use the custom video blot instead of HTML insertion
        editor.insertEmbed(range.index, "customVideo", videoUrl);
        editor.insertText(range.index + 1, "\n");
        editor.setSelection(range.index + 2, 0);

        console.log("Video inserted successfully using custom blot");
      }
    } catch (error) {
      console.error("Error uploading video:", error);
      toast.error("Failed to upload video. Please try again.");
    }
  };

  const handleInsertImage = () => {
    const fileInput = document.createElement("input");
    fileInput.setAttribute("type", "file");
    fileInput.setAttribute("accept", "image/*");
    fileInput.click();

    fileInput.onchange = async (e) => {
      const target = e.target as HTMLInputElement;
      const file = target?.files?.[0];
      if (file) {
        setIsUploading(true);
        await handleImageUpload(file);
        setIsUploading(false);
      }
    };
  };

  const handleInsertVideo = () => {
    const fileInput = document.createElement("input");
    fileInput.setAttribute("type", "file");
    fileInput.setAttribute("accept", "video/mp4,video/webm,video/ogg");
    fileInput.click();

    fileInput.onchange = async (e) => {
      const target = e.target as HTMLInputElement;
      const file = target?.files?.[0];
      if (file) {
        if (!file.type.startsWith("video/")) {
          toast.error("Please select a valid video file");
          return;
        }

        const maxSize = 100 * 1024 * 1024; // 100MB
        if (file.size > maxSize) {
          toast.error("Video file is too large. Please select a file smaller than 100MB.");
          return;
        }

        setIsUploading(true);
        const toastId = toast.loading("Uploading video... This may take a while.");

        try {
          await handleVideoUpload(file);
          toast.success("Video uploaded successfully!", { id: toastId });
        } catch (err) {
          toast.error("Failed to upload video.", { id: toastId });
          console.error("Video upload error:", err);
        } finally {
          setIsUploading(false);
        }
      }
    };
  };

  const handleHeaderImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setHeaderImage(file);
      setHeaderImagePreview(URL.createObjectURL(file));
    }
  };

  const submitHandler = async () => {
    if (!title.trim()) {
      toast.error("Title is Required");
      return;
    }

    if (!content.trim()) {
      toast.error("Content is required");
      return;
    }

    const wordCount = title.trim().split(/\s+/).length;
    if (wordCount > 50) {
      toast.error("Title should not exceed 50 words");
      return;
    }

    if (!introImage) {
      toast.error("Intro Image is required");
      return;
    }

    setSubmitLoader(true);
    const articleData = {
      title,
      content: quillRef.current?.getEditor().getContents(),
      standard,
      courseName,
      category_id: categoryIds,
      intro,
      introImage: introImage || null,
    };

    const response = await handleSubmit(articleData);
    if (response) {
      toast.success("Article Created Successfully");
      navigate("/news-blogs");
    }
    setSubmitLoader(false);
  };

  const handleGifInsert = () => {
    const fileInput = document.createElement("input");
    fileInput.setAttribute("type", "file");
    fileInput.setAttribute("accept", "image/gif");
    fileInput.click();

    fileInput.onchange = async (e) => {
      const target = e.target as HTMLInputElement;
      const file = target?.files?.[0];
      const formData = new FormData();

      if (file) {
        formData.append("file", file);
        setIsUploading(true);

        try {
          const response = await ApiService({
            method: "POST",
            endpoint: apiPaths.uploadFile,
            headers: {
              "Content-type": "multipart/form-data",
            },
            data: formData,
          });
          console.log(response);
          const gifUrl = response.file;

          const editor = quillRef.current?.getEditor();
          const range = editor?.getSelection();
          if (editor && range) {
            editor.insertEmbed(range.index, "image", gifUrl);
            editor.insertText(range.index + 1, "\n");
            editor.setSelection(range.index + 2, 0);
          }
        } catch (error) {
          console.error("Error uploading GIF:", error);
          alert("Failed to upload GIF. Please try again.");
        } finally {
          setIsUploading(false);
        }
      }
    };
  };

  const modules = {
    toolbar: [
      [{ font: [] }],
      [{ size: ["small", false, "large", "huge"] }],
      ["bold", "italic", "underline", "strike"],
      [{ color: [] }, { background: [] }],
      [{ align: [] }],
    ],
    clipboard: {
      matchVisual: false,
    },
  };

  const formats = [
    "header",
    "font",
    "size",
    "bold",
    "italic",
    "underline",
    "strike",
    "color",
    "background",
    "list",
    "bullet",
    "indent",
    "align",
    "link",
    "image",
    "customVideo", // Add customVideo to formats
  ];

  const customStyles = `
    .editor-wrapper {
      background-color: white;
      border: 1px solid #e2e8f0;
      border-radius: 0.375rem;
      max-width: 100%;
      overflow: hidden;
    }

    .quill {
      background-color: white;
      position: relative;
      display: flex;
      flex-direction: column;
      min-height: 300px;
      height: auto;
    }

    .ql-toolbar {
      background-color: white;
      border-bottom: 1px solid #e2e8f0 !important;
      border-top-left-radius: 0.375rem;
      border-top-right-radius: 0.375rem;
      flex-wrap: wrap;
      padding: 8px;
    }

    .ql-toolbar button {
      margin: 2px;
    }

    .ql-container {
      background-color: white;
      flex: 1;
      overflow: auto;
      border: none !important;
    }

    .ql-editor {
      min-height: 300px;
      height: auto;
      padding: 1rem;
      font-size: 1rem;
      line-height: 1.5;
      overflow-x: hidden;
    }

    .ql-editor img {
      max-width: 100%;
      height: auto !important;
      display: block;
      margin: 1rem auto;
      object-fit: contain;
    }

    /* Custom video styles for the new blot */
    .ql-editor video {
      max-width: 100% !important;
      width: 100% !important;
      height: auto !important;
      display: block !important;
      margin: 10px auto !important;
      border-radius: 8px !important;
    }

    .ql-editor iframe {
      max-width: 100%;
      height: auto;
      aspect-ratio: 16/9;
    }

    .ql-editor p {
      margin-bottom: 1rem;
    }

    @media (max-width: 768px) {
      .ql-toolbar {
        padding: 4px;
      }

      .ql-editor {
        padding: 0.75rem;
        font-size: 0.95rem;
      }

      .editor-wrapper {
        margin: 0 -1rem;
        border-radius: 0;
      }

      .ql-editor video {
        margin: 5px auto !important;
      }
    }
  `;

  useEffect(() => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = content;
    const textContent = tempDiv.textContent || tempDiv.innerText;

    const contentWords = textContent.trim().replace(title, "").split(/\s+/).join(" ");

    const generatedIntro = `${contentWords}`.trim();
    setIntro(generatedIntro);

    const imgMatch = content.match(/<img[^>]+src="([^">]+)"/);
    if (imgMatch && imgMatch[1]) {
      setIntroImage(imgMatch[1]);
    }
  }, [content]);

  const parseHTML = (html: string) => {
    const temp = document.createElement("div");
    temp.innerHTML = html;

    // Handle videos
    const videos = temp.getElementsByTagName("video");
    Array.from(videos).forEach((video) => {
      if (video instanceof HTMLVideoElement) {
        video.controls = true;
        video.preload = "metadata";
        video.style.width = "100%";
        video.style.maxWidth = "100%";
        video.style.height = "auto";
        video.style.display = "block";
        video.style.margin = "10px auto";
        video.style.borderRadius = "8px";
        video.style.webkitUserSelect = "none";

        // Ensure source is set properly
        if (!video.querySelector("source") && video.src) {
          const source = document.createElement("source");
          source.src = video.src;
          source.type = "video/mp4";
          video.appendChild(source);
        }
      }
    });

    // Handle images
    const images = temp.getElementsByTagName("img");
    Array.from(images).forEach((img) => {
      if (img instanceof HTMLImageElement) {
        img.style.width = "100%";
        img.style.height = "auto";
        img.style.maxWidth = "100%";
        img.style.display = "block";
        img.style.margin = "10px auto";
        img.style.borderRadius = "8px";
        img.loading = "lazy";
      }
    });

    return temp.innerHTML;
  };

  const PreviewMode = () => (
    <div className="max-w-sm mx-auto bg-white min-h-screen overflow-hidden">
      <div className="fixed top-0 left-0 right-0 bg-white z-10 shadow-sm">
        <div className="h-12 flex items-center justify-between px-4">
          <button onClick={() => setIsPreview(false)} className="text-blue-500">
            Edit
          </button>
          <span className="text-sm font-medium">Preview</span>
          <div className="w-16"></div>
        </div>
      </div>

      <div className="pt-12 px-4">
        {headerImagePreview && (
          <div className="w-full h-48 mb-4 overflow-hidden rounded-lg">
            <img src={headerImagePreview} alt="Header" className="w-full h-full object-cover" />
          </div>
        )}

        <h1 className="text-2xl font-bold mb-4 text-gray-900">{title || "Untitled Article"}</h1>

        <div
          className="prose prose-sm max-w-none"
          dangerouslySetInnerHTML={{
            __html: parseHTML(content),
          }}
          style={{
            fontSize: "16px",
            lineHeight: "1.6",
            color: "#374151",
          }}
        />
      </div>
    </div>
  );

  return (
    <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 py-4">
      <style>{customStyles}</style>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-4">
        <h2 className="text-xl sm:text-2xl font-semibold">Create Article</h2>
        <div className="flex justify-center items-center gap-2">
          {!isPreview && (
            <button
              onClick={() => navigate(-1)}
              className="flex items-center px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 w-full sm:w-auto"
            >
              <ArrowLeft className="mr-2 h-5 w-5" />
              Back
            </button>
          )}
          <button
            onClick={() => setIsPreview(!isPreview)}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 w-full sm:w-auto"
          >
            {isPreview ? "Edit" : "Preview"}
          </button>
        </div>
      </div>

      {isPreview ? (
        <PreviewMode />
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <select
              value={standard}
              onChange={(e) => setStandard(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-md"
              required
            >
              <option value="">Select Education</option>
              {standards.map((std) => (
                <option key={std.standard} value={std.standard}>
                  {std.standard}
                </option>
              ))}
            </select>

            <select
              value={courseName}
              onChange={(e) => setCourseName(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-md"
              disabled={standard.length === 0}
              required
            >
              <option value="">Select Stream</option>
              {filteredCourses.map((course) => (
                <option key={course.courseName} value={course.courseName}>
                  {course.courseName}
                </option>
              ))}
            </select>

            <Autocomplete
              multiple
              options={categories}
              getOptionLabel={(option) => option.category}
              value={categories.filter(cat => categoryIds.includes(cat._id))}
              onChange={(event, newValue) => {
                setCategoryIds(newValue.map(cat => cat._id));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={categoryIds.length === 0 ? "Select Categories" : ""}
                  variant="outlined"
                  required={categoryIds.length === 0}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      padding: '8px',
                    }
                  }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option.category}
                    {...getTagProps({ index })}
                    size="small"
                    sx={{ margin: '2px' }}
                  />
                ))
              }
              sx={{ width: '100%' }}
            />
          </div>

          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Article Title"
            className="w-full p-3 border border-gray-300 rounded-md text-base sm:text-lg"
          />

          <div className="editor-wrapper">
            <ReactQuill
              ref={quillRef}
              theme="snow"
              value={content}
              onChange={setContent}
              placeholder="Write your article here..."
              modules={modules}
              formats={formats}
            />
          </div>

          <div className="flex flex-col sm:flex-row gap-4 flex-wrap">
            <button
              onClick={handleInsertImage}
              disabled={isUploading || submitLoader}
              className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed w-full sm:w-auto"
            >
              {isUploading ? "Uploading..." : "Insert Image"}
            </button>

            <button
              onClick={handleInsertVideo}
              disabled={isUploading || submitLoader}
              className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 disabled:bg-gray-400 disabled:cursor-not-allowed w-full sm:w-auto"
            >
              {isUploading ? "Uploading..." : "Insert Video"}
            </button>

            <button
              onClick={handleGifInsert}
              disabled={isUploading || submitLoader}
              className="px-4 py-2 bg-teal-500 text-white rounded-md hover:bg-purple-600 disabled:bg-gray-400 disabled:cursor-not-allowed w-full sm:w-auto"
            >
              {isUploading ? "Uploading..." : "Insert GIF"}
            </button>

            <button
              onClick={submitHandler}
              className={`px-8 py-2 bg-green-500 text-white font-bold rounded-md hover:bg-green-600 w-full sm:w-auto ${isUploading || submitLoader ? "opacity-50 cursor-not-allowed" : ""}`}
              disabled={isUploading || submitLoader}
            >
              {submitLoader ? "Submitting.." : "Submit"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateArticle;
